import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FlightLogLifecycle } from '@@api/flightLog.shared'
import { useOrpcTanstackQueryUtils } from '../orpc'
import { useIsAdmin } from '../../hooks/useIsCompanyAdmin'

// Flight log for a trip leg, plus prefill data (route/crew/pax) and
// brought-forward totals from the aircraft's previous submitted log.
// Only pilots and admins may call this — keep it disabled for other roles.
export function useFlightLogGetForTripLeg(tripLegId?: string) {
  const orpc = useOrpcTanstackQueryUtils()

  return useQuery({
    ...orpc.flightLog.getForTripLeg.queryOptions({ input: { tripLegId: tripLegId! } }),
    enabled: !!tripLegId,
  })
}

export function useFlightLogUpsertDraft() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.flightLog.upsertDraft.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.flightLog.key() })
        // Trip legs embed a flight-log status summary — keep it fresh
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

export function useFlightLogSubmit() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.flightLog.submit.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.flightLog.key() })
        // Trip legs embed a flight-log status summary — keep it fresh
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

// All logs for one aircraft (admin or pilot) — the aircraft Flight Logs card
// and the /aircraft/$aircraftId/flight-logs list share this query.
export function useFlightLogGetByAircraft(aircraftId?: string, opts?: { enabled?: boolean }) {
  const orpc = useOrpcTanstackQueryUtils()

  return useQuery({
    ...orpc.flightLog.getByAircraft.queryOptions({ input: { aircraftId: aircraftId! } }),
    enabled: !!aircraftId && (opts?.enabled ?? true),
  })
}

// Submitted logs with unviewed deferred defects (admin-only procedure) —
// drives both the operations Updates row count and the Defects Logged drawer.
export function useFlightLogDefectsToReview(enabled = true) {
  const orpc = useOrpcTanstackQueryUtils()
  const { isAdmin } = useIsAdmin()

  return useQuery({
    ...orpc.flightLog.getDefectsToReview.queryOptions(),
    enabled: enabled && isAdmin,
  })
}

// Per-aircraft flight hours / distance totals from submitted logs. Open to
// every role, so no enabled gate. Shared by the aircraft list (fleet totals)
// and the aircraft detail page; lives under flightLog so a log submit
// invalidates it along with the rest of the flightLog cache.
export function useFlightLogAircraftStats() {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.flightLog.getAircraftStats.queryOptions())
}

export function useFlightLogMarkDefectsViewed() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.flightLog.markDefectsViewed.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.flightLog.key() })
      },
    })
  )
}

// Lean list DTO from flightLog.getMyPendingLogs (PendingFlightLogItem).
// logNumber is null for legs whose log was never started.
export interface PendingFlightLog {
  id: string
  tripLegId: string
  tripId: string
  logNumber: number | null
  lifecycle: FlightLogLifecycle
  departureAirportIcao: string
  arrivalAirportIcao: string
  departureDateTime: Date | string
  aircraftRegistration: string | null
  defectDeferred: boolean
}

// All of the current pilot's incomplete logs (drafts on any leg, plus legs
// that departed — or depart within ~2h — without a log ever being started).
// The Home flight-log card and the attention drawer share this query.
export function useFlightLogGetMyPendingLogs(enabled: boolean) {
  const orpc = useOrpcTanstackQueryUtils()

  return useQuery({
    ...orpc.flightLog.getMyPendingLogs.queryOptions(),
    enabled,
  })
}

// Lean row from flightLog.getCompanyLegs (CompanyFlightLogLegItem): one per
// trip leg in the window, with or without a log. logNumber is null when the
// log was never started.
export interface CompanyFlightLogLeg {
  id: string
  tripLegId: string
  tripId: string
  tripName: string
  logNumber: number | null
  lifecycle: FlightLogLifecycle
  tripLegStatus: string
  departureAirportIcao: string
  arrivalAirportIcao: string
  departureDateTime: Date | string
  aircraftId: string
  aircraftRegistration: string | null
  signedOffBy: string | null
  isMine: boolean
  defectDeferred: boolean
}

// Every company leg departing in [from, to) — the More → Flight Logs page.
// Pilots and admins only; pass `enabled` accordingly.
export function useFlightLogGetCompanyLegs(
  range: { from: Date; to: Date } | undefined,
  enabled: boolean
) {
  const orpc = useOrpcTanstackQueryUtils()

  return useQuery({
    ...orpc.flightLog.getCompanyLegs.queryOptions({ input: range! }),
    enabled: enabled && !!range,
  })
}
